import React from 'react';
import PropTypes from 'prop-types';

import FormQuestion from '../components/FormQuestion';
import RenderAllFormComponent from '../intake_forms/RenderAllFormComponent';

import {Stack, Paper, Typography} from 'views/components';
import {translate} from 'views/module';

const FormStep = (props) => {
  const {forms, currentFormIndex, onFormComplete, statusProgressWrap: progressWrap, onEmailCaptured} = props;

  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [answersByQuestion, setAnswersByQuestion] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [currentAnswer, setCurrentAnswer] = React.useState(null);
  const [customProgress, setCustomProgress] = React.useState(null);
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);

  const currentForm = forms[currentFormIndex];
  const sortQuestions = React.useCallback((questionMap) => {
    const questionList = Object.values(questionMap || {});
    return questionList.sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, []);

  const CustomComponent = currentForm?.component;
  const hasCustomComponent = Boolean(CustomComponent);

  const pages = !hasCustomComponent && Array.isArray(currentForm?.pages) ? currentForm.pages : null;
  const hasPages = Boolean(pages && pages.length);

  const currentPage = hasPages ? pages[currentPageIndex] : null;
  const pageQuestions = React.useMemo(() => {
    if (hasCustomComponent) {
      return [];
    }
    if (hasPages) {
      return sortQuestions(currentPage?.questions);
    }
    return sortQuestions(currentForm?.questions);
  }, [currentForm, currentPage, hasPages, hasCustomComponent, sortQuestions]);

  const renderAll = !hasCustomComponent && (hasPages ? currentPage?.renderAll === true : currentForm?.renderAll === true);
  const currentQuestion = renderAll ? null : pageQuestions[currentQuestionIndex];
  const totalQuestions = renderAll ? 1 : pageQuestions.length;
  const isLastPage = !hasPages || currentPageIndex >= pages.length - 1;
  const isLastQuestionInPage = currentQuestionIndex >= totalQuestions - 1;
  const shouldSubmit = isLastPage && isLastQuestionInPage;
  const formTitle = currentForm?.title || '';
  const isFinalForm = currentFormIndex >= forms.length - 1;
  const buttonLabel = (isFinalForm && shouldSubmit) ? translate('form.FormStep.Submit') : translate('form.FormStep.Next');
  const showPopper = currentFormIndex === 0;
  const progressLabel = React.useMemo(() => {
    let progressCurrent = 1;
    let progressTotal = 1;
    let progressMode = 'question';
    if (hasCustomComponent) {
      progressMode = 'custom';
    } else if (hasPages) {
      progressMode = 'pages';
    } else if (renderAll) {
      progressMode = 'renderAll';
    }

    switch (progressMode) {
      case 'custom':
        progressCurrent = customProgress?.current || 1;
        progressTotal = customProgress?.total || 1;
        break;
      case 'pages':
        progressCurrent = currentPageIndex + 1;
        progressTotal = pages.length;
        break;
      case 'renderAll':
        progressCurrent = 1;
        progressTotal = totalQuestions;
        break;
      default:
        progressCurrent = currentQuestionIndex + 1;
        progressTotal = totalQuestions;
        break;
    }

    return translate('form.FormStep.Progress', {
      current: progressCurrent,
      total: progressTotal,
    });
  }, [
    customProgress?.current,
    customProgress?.total,
    currentPageIndex,
    currentQuestionIndex,
    hasCustomComponent,
    hasPages,
    pages?.length,
    renderAll,
    totalQuestions,
  ]);

  React.useEffect(() => {
    setCurrentQuestionIndex(0);
    setCurrentPageIndex(0);
    setAnswersByQuestion({});
  }, [currentFormIndex, forms]);

  React.useEffect(() => {
    if (hasCustomComponent || renderAll) {
      setCurrentAnswer(null);
      return;
    }
    const currentAnswer = answersByQuestion[currentQuestion?.questionId];
    setCurrentAnswer(currentAnswer);
  }, [currentQuestionIndex, currentQuestion, renderAll, answersByQuestion]);

  React.useEffect(() => {
    if (hasCustomComponent) return;
    const emailValue = answersByQuestion.email?.questionData?.value;
    if (emailValue) {
      onEmailCaptured?.(emailValue);
    }
  }, [answersByQuestion, hasCustomComponent, onEmailCaptured]);

  const submitCurrentForm = async (mergedAnswers) => {
    progressWrap(async () => {
      setIsSubmitting(true);
      try {
        onFormComplete(mergedAnswers);
      } catch (error) {
        console.error('Failed to submit form:', error);
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  const handleNext = async (answer) => {
    if (!currentQuestion) return;

    const mergedAnswers = {...answersByQuestion, [currentQuestion.questionId]: answer};
    setAnswersByQuestion(mergedAnswers);

    const isLastQuestion = currentQuestionIndex >= totalQuestions - 1;

    if (isLastQuestion) {
      if (hasPages && currentPageIndex < pages.length - 1) {
        setCurrentPageIndex((idx) => idx + 1);
        setCurrentQuestionIndex(0);
        return;
      }
      await submitCurrentForm(mergedAnswers);
      return;
    }

    setCurrentQuestionIndex((idx) => idx + 1);
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((idx) => idx - 1);
      return;
    }
    if (hasPages && currentPageIndex > 0) {
      const prevPageIndex = currentPageIndex - 1;
      const prevQuestions = sortQuestions(pages[prevPageIndex]?.questions);
      setCurrentPageIndex(prevPageIndex);
      setCurrentQuestionIndex(Math.max(prevQuestions.length - 1, 0));
    }
  };

  const handleRenderAllChange = (questionId, value) => {
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        bioQuestionId: questionId,
        fieldType: 'short_text',
        questionData: {
          value: value ?? '',
        },
      },
    }));
  };

  const handleRenderAllRadioSelect = (questionId, option) => {
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        bioQuestionId: questionId,
        fieldType: 'short_text',
        questionData: {
          value: option.isOther ? 'other' : option.value,
        },
      },
    }));
  };

  const handleRenderAllCheckboxToggle = (questionId, optionValue, checked) => {
    const isObjectQuestion = ['careSettings', 'accreditations', 'careTasks'].includes(questionId);
    setAnswersByQuestion((prev) => {
      const currentValue = prev[questionId]?.questionData?.value;
      const currentValues = Array.isArray(currentValue?.selected) ?
        currentValue.selected :
        (Array.isArray(currentValue) ? currentValue : []);
      const nextValues = checked ?
        [...currentValues, optionValue] :
        currentValues.filter((value) => value !== optionValue);
      const nextValue = isObjectQuestion ?
        {selected: nextValues, otherText: prev[questionId]?.questionData?.value?.otherText || ''} :
        nextValues;
      return {
        ...prev,
        [questionId]: {
          bioQuestionId: questionId,
          fieldType: isObjectQuestion ? 'object' : 'checkbox',
          questionData: {
            value: nextValue,
          },
        },
      };
    });
  };

  const handleRenderAllOtherTextChange = (questionId, value) => {
    const isObjectQuestion = ['careSettings', 'accreditations', 'careTasks'].includes(questionId);
    setAnswersByQuestion((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        bioQuestionId: questionId,
        fieldType: isObjectQuestion ? 'object' : (prev[questionId]?.fieldType || 'checkbox'),
        questionData: {
          value: isObjectQuestion ?
            {
              selected: prev[questionId]?.questionData?.value?.selected || [],
              otherText: value ?? '',
            } :
            value ?? '',
        },
      },
    }));
  };

  const handleRenderAllSubmit = async () => {
    if (hasPages && currentPageIndex < pages.length - 1) {
      setCurrentPageIndex((idx) => idx + 1);
      return;
    }
    await submitCurrentForm(answersByQuestion);
  };

  const renderAllNextDisabled = renderAll && pageQuestions.some((question) => {
    const required = question?.questionData?.required;
    if (!required) return false;
    const answer = answersByQuestion[question.questionId];
    if (
      question.fieldType === 'text' ||
      question.fieldType === 'phoneNumber' ||
      question.fieldType === 'email' ||
      question.fieldType === 'dropdown'
    ) {
      const value = answer?.questionData?.value || '';
      return !Boolean(value.trim());
    }
    if (question.fieldType === 'radio') {
      const rawValue = answer?.questionData?.value;
      if (rawValue === 'other') {
        return true;
      }
      if (rawValue && !question.options?.some((opt) => opt.value === rawValue)) {
        return !Boolean(String(rawValue).trim());
      }
      return !Boolean(rawValue);
    }
    if (question.fieldType === 'checkbox') {
      const rawValue = answer?.questionData?.value;
      let values = [];
      if (Array.isArray(rawValue?.selected)) {
        values = rawValue.selected;
      } else if (Array.isArray(rawValue)) {
        values = rawValue;
      }
      const otherText = rawValue?.otherText || '';
      if (values.includes('other')) {
        return !Boolean(String(otherText).trim());
      }
      return values.length === 0;
    }
    return false;
  });

  return (
    <Stack spacing={{xs: 2, sm: 4}}>
      <Paper
        sx={{
          borderRadius: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: {xs: 1.5, sm: 2},
          px: 3,
          backgroundColor: '#F0EAE5',
          boxShadow: 'none',
        }}
      >
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: {xs: '16px', sm: '20px'},
            color: '#2D2925',
            fontFamily: 'Spectral Light',
            lineHeight: '128%',
            letterSpacing: '-2%',
          }}
        >
          {formTitle}
        </Typography>
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: {xs: '16px', sm: '20px'},
            color: '#2D2925',
            fontFamily: 'Spectral Light',
            lineHeight: '128%',
            letterSpacing: '-2%',
          }}
        >
          {progressLabel}
        </Typography>
      </Paper>
      {
        hasCustomComponent && (
          <CustomComponent
            onSubmit={submitCurrentForm}
            isSubmitting={isSubmitting}
            onEmailCaptured={onEmailCaptured}
            onProgressChange={setCustomProgress}
            showPopper={showPopper}
            isFinalForm={isFinalForm}
          />
        )
      }
      {
        !hasCustomComponent && renderAll && (
          <RenderAllFormComponent
            pageQuestions={pageQuestions}
            answersByQuestion={answersByQuestion}
            onTextChange={handleRenderAllChange}
            onRadioSelect={handleRenderAllRadioSelect}
            onCheckboxToggle={handleRenderAllCheckboxToggle}
            onOtherTextChange={handleRenderAllOtherTextChange}
            onSubmit={handleRenderAllSubmit}
            onPrevious={handlePrevious}
            isSubmitting={isSubmitting}
            isNextDisabled={renderAllNextDisabled}
            hasPrevious={hasPages && currentPageIndex > 0}
            hasNextPage={hasPages && currentPageIndex < pages.length - 1}
            isFinalSubmit={shouldSubmit}
          />
        )
      }
      {
        !hasCustomComponent && !renderAll && currentQuestion && (
          <FormQuestion
            question={currentQuestion}
            buttonLabel={buttonLabel}
            onNext={handleNext}
            onPrevious={handlePrevious}
            answer={currentAnswer}
            isLoading={isSubmitting}
            isSubmit={shouldSubmit}
            showPopper={showPopper}
          />
        )
      }
    </Stack>
  );
};

FormStep.propTypes = {
  forms: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    formKey: PropTypes.string,
    questions: PropTypes.object,
    formVersion: PropTypes.number,
    description: PropTypes.string,
    component: PropTypes.elementType,
    renderAll: PropTypes.bool,
    pages: PropTypes.arrayOf(PropTypes.shape({
      questions: PropTypes.object,
      renderAll: PropTypes.bool,
    })),
  })).isRequired,
  currentFormIndex: PropTypes.number.isRequired,
  onFormComplete: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
  onEmailCaptured: PropTypes.func,
};

export default React.memo(FormStep);
